import Vue from 'vue'
import { mapFields } from 'vuex-map-fields'

const VideoModal = {
    name: 'VideoModal',
    data() {
        return {
            player: null,
            lastTime: 0,
        }
    },
    props: {
        show: Boolean,
        controls: {
            type: Boolean,
            default: true
        },
        videoUrl: {
            type: String,
            default: ''
        }
    },
    computed: {
        isVimeoVideo() {
            return this.videoUrl.includes('vimeo.com')
        },
        embedUrl() {
            if (this.isVimeoVideo) {
                const vimeoId = this.videoUrl.match(/vimeo\.com\/(\d+)/)?.[1]
                if (vimeoId) {
                    return `https://player.vimeo.com/video/${vimeoId}`
                }
            }
            return this.videoUrl
        },
        ...mapFields('onboarding_state', [
            'displayedVideoKeys',
            'videoCompleted',
            'showModal',
            'showModalForced',
            'currentLessonData',
            'thumbnailUrl'
        ])
    },
    beforeDestroy() {
        if (this.$styleElement) {
            this.$styleElement.remove()
        }
    },
    methods: {
        handleTimeUpdate(event) {
            if (!this.videoCompleted) {
                const video = event.target;
                const currentTime = video.currentTime;
                
                if (video.duration - currentTime <= 0.5) {
                    this.handleVideoEnd();
                    return;
                }
                
                if (Math.abs(currentTime - this.lastTime) > 1) {
                    video.currentTime = this.lastTime;
                } else {
                    this.lastTime = currentTime;
                }
            }
        },
        async handleVideoEnd() {
            if(this.showModal) {
                this.$store.commit('onboarding_state/setVideoCompleted', true);
                const currentPath = window.location.pathname.replace(/^\//, '')
                const currentSearch = window.location.search
                const fullKey = `${currentPath}${currentSearch}`
                if(!localStorage.getItem(`video_popup_disabled_${fullKey}`)) {
                    // here we mark the video as watched in the localstorage so if a video is already watched for this page then we don't show the video on page load
                    localStorage.setItem(`video_popup_disabled_${fullKey}`, 'true');
                }
                
                if(this.currentLessonData?.isCompleted) return;
                await this.$http.post('/api/v1/course-complete/markCourseLessonAsCompleted/' + this.currentLessonData.lessonId, {
                    task_id: this.currentLessonData.taskId
                });
                this.currentLessonData.isCompleted = true;

                
            }
        },
        async initVimeoPlayer() {
            try {
                if (!window.Vimeo) {
                    await this.loadVimeoScript();
                }
                
                const player = new Vimeo.Player(this.$refs.vimeoPlayer, {
                    controls: true,
                    playsinline: true
                });
                
                this.player = player;
                this.setupVimeoEventListeners(player);
            } catch (error) {
                console.error('Failed to initialize Vimeo player:', error);
            }
        },
        setupVimeoEventListeners(player) {
            player.on('ended', () => {
                this.handleVideoEnd();
            });

            player.on('timeupdate', ({ seconds, duration }) => {
                if (!this.videoCompleted && duration) {
                    if (duration - seconds <= 0.5) {
                        this.handleVideoEnd();
                        return;
                    }

                    if (Math.abs(seconds - this.lastTime) <= 1) {
                        this.lastTime = seconds;
                    } else {
                        player.setCurrentTime(this.lastTime);
                    }
                }
            });

            if (!this.videoCompleted) {
                player.on('seeking', ({ seconds }) => {
                    if (!this.videoCompleted) {
                        player.setCurrentTime(this.lastTime);
                    }
                });
            }
        },
        handleStopShowingPopup() {
            const currentPath = window.location.pathname.replace(/^\//, '')
            const currentSearch = window.location.search
            const fullKey = `${currentPath}${currentSearch}`
            localStorage.setItem(`video_popup_disabled_${fullKey}`, 'true');
            this.$store.commit('onboarding_state/setShowModal', false);
            this.$emit('close');
        },
    },
    render(h) {
        const closeButton = h('button', {
            staticClass: 'close-button',
            on: { click: this.handleStopShowingPopup }
        }, [
            h('i', { 
                staticClass: 'mdi mdi-close',
                style: {
                    fontSize: '24px'
                }
            })
        ]);

        const videoElement = !this.isVimeoVideo ? h('video', {
            ref: 'videoPlayer',
            staticClass: 'video-player',
            attrs: {
                src: this.videoUrl,
                controls: this.controls,
                playsinline: true
            },
            on: {
                timeupdate: this.handleTimeUpdate,
                ended: this.handleVideoEnd
            }
        }) : null;

        return h('div', {
            staticClass: 'video-modal-overlay',
            style: {
                display: this.show ? 'flex' : 'none'
            },
            on: {
                click: (e) => {
                    if (e.target === e.currentTarget && this.videoCompleted) {
                        this.handleStopShowingPopup();
                    }
                }
            }
        }, [
            h('div', { staticClass: 'video-modal-container' }, [
                h('div', { staticClass: 'video-modal-content' }, [
                    h('div', { staticClass: 'close-button-container' }, [
                        closeButton
                    ]),
                    this.isVimeoVideo
                        ? h('iframe', {
                            ref: 'vimeoPlayer',
                            attrs: {
                                src: this.embedUrl,
                                width: '100%',
                                height: '450',
                                frameborder: '0',
                                allow: 'autoplay; fullscreen',
                                allowfullscreen: true
                            }
                        })
                        : videoElement,
                    !this.videoCompleted
                        ? h('div', { staticClass: 'video-notice' }, ['Please watch the entire video'])
                        : null
                ])
            ])
        ]);
    },
    mounted() {
        const styleElement = document.createElement('style');
        styleElement.textContent = `
            .video-modal-overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.8);
                z-index: 9999;
                display: flex !important;
                justify-content: center;
                align-items: center;
            }
            .video-modal-container {
                width: 90%;
                max-width: 800px;
                background: var(--secondary-background);
                border-radius: var(--border-radius-primary);
                padding: 20px;
                margin: auto;
            }
            .video-modal-content {
                width: 100%;
            }
            .video-player {
                width: 100%;
                height: auto;
                max-height: 450px;
            }
            .button-container {
                display: flex;
                gap: 10px;
                margin-top: 10px;
                flex-wrap: wrap;
            }
            .close-button {
                flex: 1;
                padding: 8px 16px;
                border: none;
                border-radius: var(--border-radius-secondary) !important;
                cursor: pointer;
            }
            .video-notice {
                text-align: center;
                margin-top: 10px;
                color: var(--secondary-text);
            }
            iframe {
                border-radius: var(--border-radius-secondary) !important;
                margin-bottom: 10px;
                display: block;
                margin: 0 auto;
            }
            .video-help-button {
                position: fixed;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 15px 30px;
                background: #fff;
                border: 1px solid #ddd;
                border-radius: 25px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                cursor: pointer;
                z-index: 1000;
                max-width: 200px;
                max-height: 200px;
                width: 100%;
                transition: all 0.3s ease;
            }
            
            .video-help-button svg {
                width: 24px;
                height: 24px;
                flex-shrink: 0;
            }
            
            .video-help-button span {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .video-help-button:hover {
                background: var(--quaternary-background);
                transform: translateX(-50%) scale(1.05);
                box-shadow: var(--box-shadow-primary);
            }
            
            .stop-showing-button {
                flex: 1;
                padding: 8px 16px;
                border: none;
                border-radius: var(--border-radius-primary);
                cursor: pointer;
                background: var(--quaternary-background);
                color: var(--secondary-text);
            }
            .close-button-container {
                position: relative;
                text-align: right;
                padding: 10px;
            }
            
            .close-button {
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 5px;
                color: #666;
                transition: color 0.3s ease;
            }
            
            .close-button:hover {
                color: var(--primary-text);
            }
            
            .video-modal-container {
                position: relative;
                width: 90%;
                max-width: 800px;
                background: var(--secondary-background);
                border-radius: var(--border-radius-primary);
                overflow: hidden;
            }
            
            .video-modal-content {
                width: 100%;
            }
        `;
        document.head.appendChild(styleElement);
        this.$styleElement = styleElement;

        if (this.isVimeoVideo) {
            const script = document.createElement('script');
            script.src = 'https://player.vimeo.com/api/player.js';
            script.onload = () => this.initVimeoPlayer();
            document.head.appendChild(script);
        }
    }
}

// Add static property to track if API call has been made
let incompleteLessonsPromise = null;

export default {
    data() {
        return {
            videoInstance: null,
            currentLessonVideo: null,
            helpButton: null,
        }
    },
    computed: {
        ...mapFields('onboarding_state', [
            'displayedVideoKeys',
            'videoCompleted',
            'showModal',
            'showModalForced',
            'currentLessonData',
            'thumbnailUrl'
        ])
    },
    methods: {
        async fetchVideoThumbnail(videoUrl) {
            if (!videoUrl) return null;
            
            try {
                if (videoUrl.includes('vimeo.com')) {
                    const vimeoId = videoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
                    if (vimeoId) {
                        const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`);
                        const data = await response.json();
                        return data.thumbnail_url;
                    }
                }
                return null;
            } catch (error) {
                console.error('Error fetching video thumbnail:', error);
                return null;
            }
        },
        async handleUserVideoDisplay() {
            const currentPath = window.location.pathname.replace(/^\//, '');
            const currentSearch = window.location.search;
            const fullKey = `${currentPath}${currentSearch}`;
            
            if (this.displayedVideoKeys?.[fullKey]) {
                return
            }
            await this.markVideoKeyDisplayed(fullKey)
            try {



                const response = await this.$http.get('/api/v1/onboarding/getIncompleteLessons');
                if (response.data?.onboarding_lessons?.length) {
                    const matchingLesson = response.data.onboarding_lessons.find(lesson => {
                        const taskPath = lesson.task_link.replace(/^\//, '').replace(/\/$/, '');
                        const [path, query] = taskPath.split('?');
                        const normalizedCurrentPath = currentPath.replace(/\/$/, '');
                        return normalizedCurrentPath === path && (!query || currentSearch.includes(query));
                    });

                    if (matchingLesson) {
                        const thumbnail = await this.fetchVideoThumbnail(matchingLesson.lesson_link);
                        
                        this.currentLessonData = {
                            videoUrl: matchingLesson.lesson_link,
                            lessonId: matchingLesson.lesson_id,
                            taskId: matchingLesson.task_id,
                            isTaskCompleted: matchingLesson.is_task_completed,
                            isCompleted: matchingLesson.is_completed === 'yes' || false,
                            video_cta_label: matchingLesson.video_cta_label || 'Click here to view the help video'
                        };
                        
                        this.thumbnailUrl = thumbnail;
                        
                        if(this.currentLessonData?.isCompleted) {
                            this.videoCompleted = true;
                        }
                        this.currentLessonVideo = matchingLesson.lesson_link;
                        if(localStorage.getItem(`video_popup_disabled_${fullKey}`) === 'true') {
                            this.createHelpButton();
                            return;
                        }
                        // in all other cases if task or video is completed or not then show the video
                        this.showVideo({
                            controls: true,
                            videoUrl: matchingLesson.lesson_link
                        });
                        
                    }
                }
            } catch (error) {
                console.error('Error fetching incomplete lessons:', error);
            } finally {
                incompleteLessonsPromise = null;
            }
        },
        handleHelpButtonClick() {
            if (this.currentLessonData) {
                this.showModalForced = true;
                
            }
        },
        async markVideoKeyDisplayed(key) {
            await this.$store.commit('onboarding_state/markVideoKeyDisplayed', key)
        },
        showVideo(config = {}) {
            this.$store.commit('onboarding_state/setShowModal', true);
            this.createVideoModal({
                controls: config.controls ?? true,
                videoUrl: config.videoUrl ?? ''
            });
        },
        handleModalClose() {
            this.destroyVideoModal();
            this.createHelpButton();
        },
        createVideoModal(config) {
            this.destroyVideoModal();

            const modalDiv = document.createElement('div');
            document.body.appendChild(modalDiv);

            const VideoModalConstructor = Vue.extend(VideoModal);
            this.videoInstance = new VideoModalConstructor({
                store: this.$store,
                propsData: {
                    show: true,
                    ...config
                }
            });

            this.videoInstance.$on('close', this.handleModalClose);
            this.videoInstance.$mount(modalDiv);
        },
        destroyVideoModal() {
            if (this.videoInstance) {
                this.videoInstance.$destroy();
                this.videoInstance.$el.remove();
                this.videoInstance = null;
                this.$store.commit('onboarding_state/setShowModal', false);
            }
        },
        createHelpButton() {
            // Remove any existing help sections
            const existingSection = document.querySelector('.video-help-section');
            if (existingSection) {
                existingSection.remove();
            }

            const container = document.querySelector('.kt-container');
            if (!container) {
                console.warn('Container not found for video help section');
                return;
            }

            const helpSection = document.createElement('section');
            helpSection.className = 'video-help-section';
            helpSection.style.cssText = `
                margin: 10px auto 8px auto;
                padding: 10px;
                background: var(--secondary-background);
                border-radius: var(--border-radius-primary);
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                cursor: pointer;
                transition: all 0.3s ease;
                width: 100%;
                max-width: 800px;
                display: flex;
                justify-content: center;
                ${window.location.pathname === '/agents/pipeline/grid' ? 'margin-bottom: 17px;' : ''}
            `;

            const contentWrapper = document.createElement('div');
            contentWrapper.style.cssText = `
                display: flex;
                align-items: center;
                gap: 20px;
                width: 100%;
                flex-wrap: wrap;
                justify-content: center;
            `;

            const thumbnailContainer = document.createElement('div');
            thumbnailContainer.style.cssText = `
                position: relative;
                width: 180px;
                height: 100px;
                background: var(--quaternary-background);
                border-radius: var(--border-radius-primary);
                overflow: hidden;
                flex: 0 0 180px;
            `;

            // Add thumbnail image if available
            if (this.thumbnailUrl) {
                const thumbnailImg = document.createElement('img');
                thumbnailImg.src = this.thumbnailUrl;
                thumbnailImg.style.cssText = `
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                `;
                thumbnailContainer.appendChild(thumbnailImg);
            }

            // Play button overlay
            const playButton = document.createElement('div');
            playButton.style.cssText = `
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 30px;
                height: 30px;
                background: rgba(0, 0, 0, 0.7);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
            `;

            playButton.innerHTML = `
                <svg viewBox="0 0 24 24" fill="#fff" width="18" height="18">
                    <polygon points="10 8 16 12 10 16 10 8"/>
                </svg>
            `;

            const textContainer = document.createElement('div');
            textContainer.style.cssText = `
                text-align: left;
                flex: 1;
                min-width: 200px;
            `;

            const helpText = document.createElement('p');
            helpText.textContent = this.currentLessonData?.video_cta_label;
            helpText.classList.add('help-text');
            helpText.style.cssText = `
                margin: 0;
                font-size: 14px;
                color: var(--primary-text);
                font-weight: 500;
                word-wrap: break-word;
            `;

            // Media query for mobile devices
            const style = document.createElement('style');
            style.textContent = `
                @media (max-width: 576px) {
                    .video-help-section {
                        margin: 10px 15px;
                        padding: 10px;
                    }
                    .video-help-section .content-wrapper {
                        gap: 10px;
                    }
                    .video-help-section .thumbnail-container {
                        width: 100%;
                        height: 200px;
                    }
                    .video-help-section .text-container {
                        text-align: center;
                        width: 100%;
                    }
                    .help-text{
                        text-align:center;
                    }
                }
            `;
            document.head.appendChild(style);

            // Hover effect
            helpSection.addEventListener('mouseenter', () => {
                helpSection.style.transform = 'translateY(-2px)';
                helpSection.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            });

            helpSection.addEventListener('mouseleave', () => {
                helpSection.style.transform = 'translateY(0)';
                helpSection.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            });

            // Click handler
            helpSection.addEventListener('click', () => {
                if (this.currentLessonData) {
                    this.handleHelpButtonClick();
                } else {
                    console.warn('No lesson data available');
                }
            });

            // Add classes for styling
            thumbnailContainer.classList.add('thumbnail-container');
            textContainer.classList.add('text-container');
            contentWrapper.classList.add('content-wrapper');
            helpText.classList.add('help-text');

            // Assemble the section
            thumbnailContainer.appendChild(playButton);
            textContainer.appendChild(helpText);
            contentWrapper.appendChild(thumbnailContainer);
            contentWrapper.appendChild(textContainer);
            helpSection.appendChild(contentWrapper);

            // Prepend to container
            container.insertBefore(helpSection, container.firstChild);
            this.helpButton = helpSection;
        },
        destroyHelpButton() {
            const existingSection = document.querySelector('.video-help-section');
            if (existingSection) {
                existingSection.remove();
            }
            const existingStyle = document.querySelector('style[data-video-help]');
            if (existingStyle) {
                existingStyle.remove();
            }
            this.helpButton = null;
        },
        handleStopShowingPopup() {
            const currentPath = window.location.pathname.replace(/^\//, '')
            const currentSearch = window.location.search
            const fullKey = `${currentPath}${currentSearch}`
            localStorage.setItem(`video_popup_disabled_${fullKey}`, 'true');
            this.$store.commit('onboarding_state/setShowModal', false);
            this.destroyVideoModal();
            this.createHelpButton();
        }
    },
    watch: {
        showModal(newVal) {
            if (!newVal) {
                this.showModalForced = false;
                this.destroyVideoModal();
            }
        },
        showModalForced(newVal) {
            if (newVal && this.currentLessonData) {
                this.$store.commit('onboarding_state/setShowModal', true);
                this.createVideoModal({
                    controls: true,
                    videoUrl: this.currentLessonData.videoUrl
                });
            }
        }
    },
    beforeDestroy() {
        incompleteLessonsPromise = null;
        if (this.$styleElement) {
            this.$styleElement?.remove?.();
        }
        this.destroyVideoModal();
    }
} 